import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { roleLabels } from '../../mocks/stocks';
import { checkPermission, PERMISSIONS } from '../../utils/permissions';
import { api } from '../../services/api';
import { useBranding } from '../../context/BrandingContext';

interface SidebarProps {
  role: string;
  nom: string;
  collapsed?: boolean;
  onToggle?: () => void;
}

interface NavItemDef {
  label: string;
  icon: string;
  path: string;
  roles: string[];
  permission?: string;
}

interface NavGroup {
  label: string | null;
  items: NavItemDef[];
}

const navGroups: NavGroup[] = [
  {
    label: null,
    items: [
      { label: 'Tableau de bord', icon: 'ri-dashboard-2-line', path: '/dashboard', roles: ['vendeur', 'responsable_atelier', 'gerant'] },
    ],
  },
  {
    label: 'OPÉRATIONS',
    items: [
      { label: 'Nouvelle commande', icon: 'ri-add-circle-line', path: '/commande/nouvelle', roles: ['vendeur', 'gerant'], permission: 'cmd_creer' },
      { label: 'Vente tissu', icon: 'ri-shopping-bag-line', path: '/vente-tissu', roles: ['vendeur', 'gerant'], permission: 'vente_creer' },
      { label: 'Mesures clients', icon: 'ri-pencil-ruler-2-line', path: '/mesures', roles: ['vendeur', 'gerant', 'responsable_atelier'], permission: 'mesure_voir' },
      { label: 'Historique clients', icon: 'ri-history-line', path: '/clients/historique', roles: ['vendeur', 'gerant'], permission: 'client_voir' },
      { label: 'Gestion Production', icon: 'ri-command-line', path: '/production', roles: ['gerant', 'responsable_atelier'], permission: 'dash_atelier' },
    ],
  },
  {
    label: 'GESTION',
    items: [
      { label: 'Finances & Recettes', icon: 'ri-wallet-3-line', path: '/finances', roles: ['gerant'], permission: 'ca_voir' },
      { label: 'Stocks & Tissus', icon: 'ri-stack-line', path: '/stocks', roles: ['gerant', 'responsable_atelier'], permission: 'stock_voir' },
      { label: 'Gestion Fournisseurs', icon: 'ri-truck-line', path: '/fournisseurs', roles: ['gerant'], permission: 'fourn_voir' },
      { label: 'Paie & Personnel', icon: 'ri-team-line', path: '/paie', roles: ['gerant'], permission: 'paie_voir' },
      { label: 'Rapports', icon: 'ri-bar-chart-2-line', path: '/rapports', roles: ['gerant'], permission: 'rapport_personnel' },
    ],
  },
  {
    label: 'CONFIGURATION',
    items: [
      { label: 'Catalogue modèles', icon: 'ri-layout-masonry-line', path: '/catalogue-modeles', roles: ['gerant'], permission: 'stock_modifier' },
      { label: 'Types de couture', icon: 'ri-scissors-2-line', path: '/parametres/habit-types', roles: ['gerant'], permission: 'stock_modifier' },
      { label: 'Config mesures', icon: 'ri-ruler-2-line', path: '/parametres/mesures', roles: ['gerant'], permission: 'config_mesures' },
      { label: 'Config personnel', icon: 'ri-user-star-line', path: '/parametres/personnel', roles: ['gerant'], permission: 'user_voir' },
      { label: 'Entreprise', icon: 'ri-building-line', path: '/parametres/entreprise', roles: ['gerant'], permission: 'config_entreprise' },
      { label: 'Utilisateurs', icon: 'ri-user-settings-line', path: '/utilisateurs', roles: ['gerant'], permission: 'user_gerer' },
      { label: 'Journal d\'Audit', icon: 'ri-history-line', path: '/parametres/audit', roles: ['gerant'], permission: 'user_gerer' },
    ],
  },
];

const superadminGroups: NavGroup[] = [
  {
    label: null,
    items: [
      { label: 'Tableau de bord', icon: 'ri-dashboard-2-line', path: '/dashboard', roles: ['superadmin'] },
    ],
  },
  {
    label: 'OPÉRATIONS',
    items: [
      { label: 'Nouvelle commande', icon: 'ri-add-circle-line', path: '/commande/nouvelle', roles: ['superadmin'] },
      { label: 'Vente tissu', icon: 'ri-receipt-line', path: '/vente-tissu', roles: ['superadmin'] },
      { label: 'Mesures clients', icon: 'ri-pencil-ruler-2-line', path: '/mesures', roles: ['superadmin'] },
      { label: 'Historique clients', icon: 'ri-history-line', path: '/clients/historique', roles: ['superadmin'] },
      { label: 'Gestion Production', icon: 'ri-command-line', path: '/production', roles: ['superadmin'] },
    ],
  },
  {
    label: 'GESTION',
    items: [
      { label: 'Finances & Recettes', icon: 'ri-wallet-3-line', path: '/finances', roles: ['superadmin'] },
      { label: 'Stocks & Tissus', icon: 'ri-stack-line', path: '/stocks', roles: ['superadmin'] },
      { label: 'Gestion Fournisseurs', icon: 'ri-truck-line', path: '/fournisseurs', roles: ['superadmin'] },
      { label: 'Paie & Personnel', icon: 'ri-team-line', path: '/paie', roles: ['superadmin'] },
      { label: 'Rapports', icon: 'ri-bar-chart-2-line', path: '/rapports', roles: ['superadmin'] },
    ],
  },
  {
    label: 'CONFIGURATION',
    items: [
      { label: 'Catalogue modèles', icon: 'ri-layout-masonry-line', path: '/catalogue-modeles', roles: ['superadmin'] },
      { label: 'Types de couture', icon: 'ri-scissors-2-line', path: '/parametres/habit-types', roles: ['superadmin'] },
      { label: 'Config mesures', icon: 'ri-ruler-2-line', path: '/parametres/mesures', roles: ['superadmin'] },
      { label: 'Config personnel', icon: 'ri-user-star-line', path: '/parametres/personnel', roles: ['superadmin'] },
      { label: 'Entreprise', icon: 'ri-building-line', path: '/parametres/entreprise', roles: ['superadmin'] },
      { label: 'Utilisateurs', icon: 'ri-user-settings-line', path: '/utilisateurs', roles: ['superadmin'] },
      { label: 'Journal d\'Audit', icon: 'ri-history-line', path: '/parametres/audit', roles: ['superadmin'] },
    ],
  },
];

export default function Sidebar({ role, nom, collapsed = false, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, appVersion } = useBranding();
  const [userPermissions, setUserPermissions] = useState<string[]>([]);

  useEffect(() => {
    const refreshPermissions = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
        const permsData = await api.getMyPermissions(token);
        const perms = Array.isArray(permsData) ? permsData.map((p: string) => p.toLowerCase()) : [];
        setUserPermissions(perms);
        
        const userStr = localStorage.getItem('atelier_bassoum_user');
        if (userStr) {
          const user = JSON.parse(userStr);
          user.permissions = perms;
          localStorage.setItem('atelier_bassoum_user', JSON.stringify(user));
        }
      } catch (err) {
        console.error("Failed to refresh permissions", err);
      }
    };
    refreshPermissions();
    
    // Listen for manual triggers (e.g. from PermissionsModal)
    const handleManualRefresh = () => refreshPermissions();
    window.addEventListener('permissionsChanged', handleManualRefresh);
    
    // Refresh periodically
    const interval = setInterval(refreshPermissions, 30000); // 30s
    return () => {
      window.removeEventListener('permissionsChanged', handleManualRefresh);
      clearInterval(interval);
    };
  }, [role, nom]);

  const isSuperAdmin = role === 'superadmin';
  const isInPanelAdmin = isSuperAdmin && location.pathname.startsWith('/dashboard/superadmin');

  const groups = isSuperAdmin
    ? superadminGroups
    : navGroups.map(g => ({
        ...g,
        items: g.items.filter(item => {
          if (item.permission) {
             // For superadmin, always true. For others, check userPermissions state.
             if (isSuperAdmin) return true;
             return userPermissions.includes(item.permission.toLowerCase());
          }
          return item.roles.includes(role);
        }),
      })).filter(g => g.items.length > 0);

  const handleLogout = () => {
    localStorage.removeItem('atelier_bassoum_user');
    localStorage.removeItem('atelier_bassoum_token');
    navigate('/login');
  };

  const activeClass = isSuperAdmin
    ? 'bg-amber-400/30 text-amber-200 font-semibold'
    : 'bg-foret-400 text-white font-semibold';

  const inactiveClass = isSuperAdmin
    ? 'text-amber-200 hover:bg-amber-700/50 hover:text-white'
    : 'text-foret-100 hover:bg-foret-500 hover:text-white';

  const sectionLabelClass = isSuperAdmin
    ? 'text-amber-500/60'
    : 'text-foret-300/70';

  const dividerClass = isSuperAdmin ? 'border-amber-700/50' : 'border-foret-500/50';

  return (
    <aside
      className={`flex flex-col h-screen text-white transition-all duration-300 md:relative fixed inset-y-0 left-0 z-[110] ${
        isSuperAdmin ? 'bg-gradient-to-b from-amber-900 to-amber-800' : 'bg-foret-600'
      } ${collapsed ? '-translate-x-full md:translate-x-0 md:w-16' : 'translate-x-0 w-60'} flex-shrink-0 shadow-2xl md:shadow-none`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b ${isSuperAdmin ? 'border-amber-700' : 'border-foret-500'} ${collapsed ? 'justify-center' : ''}`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isSuperAdmin ? 'bg-amber-400' : 'bg-or-400'}`}>
          <i className={`${isSuperAdmin ? 'ri-shield-star-line text-amber-900' : 'ri-scissors-2-line text-foret-700'} text-lg`} />
        </div>
        {!collapsed && (
          <div>
            <p className="font-playfair font-bold text-base text-white leading-tight">{branding.nom}</p>
            {isSuperAdmin
              ? <span className="text-xs bg-amber-400 text-amber-900 font-bold px-1.5 py-0.5 rounded-full">SUPERADMIN</span>
              : <p className="text-foret-200 text-xs">Bamako, Mali</p>
            }
          </div>
        )}
      </div>

      {/* Superadmin panel button / Return to operations */}
      {isSuperAdmin && !collapsed && (
        <button
          onClick={() => navigate(isInPanelAdmin ? '/dashboard' : '/dashboard/superadmin')}
          className={`mx-3 mt-3 flex items-center justify-between px-3 py-2 rounded-xl border cursor-pointer transition-all text-sm font-semibold whitespace-nowrap ${
            isInPanelAdmin
              ? 'bg-amber-400 text-amber-900 border-amber-400'
              : 'bg-amber-400/20 border-amber-400/30 text-amber-200 hover:bg-amber-400/30'
          }`}
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-star-line" />
            </div>
            {isInPanelAdmin ? 'Panel Admin' : 'Aller au Panel Admin'}
          </div>
          {isInPanelAdmin && (
            <i className="ri-logout-circle-r-line opacity-70 hover:opacity-100 transition-opacity" title="Retour au Dashboard" />
          )}
        </button>
      )}

      {/* User info */}
      {!collapsed && (
        <div className={`px-4 py-3 border-b ${isSuperAdmin ? 'border-amber-700' : 'border-foret-500'}`}>
          <p className={`text-xs uppercase tracking-wider mb-0.5 ${isSuperAdmin ? 'text-amber-400' : 'text-foret-200'}`}>Connecté</p>
          <p className="text-sm font-semibold text-white leading-tight truncate">{nom}</p>
          <p className={`text-xs ${isSuperAdmin ? 'text-amber-300' : 'text-or-300'}`}>{roleLabels[role] || role}</p>
        </div>
      )}

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-2">
        {groups.map((group, gIdx) => (
          <div key={gIdx}>
            {group.label && !collapsed && (
              <div className={`px-4 pt-4 pb-1 flex items-center gap-2`}>
                <span className={`text-xs font-bold tracking-widest uppercase ${sectionLabelClass}`}>
                  {group.label}
                </span>
                <div className={`flex-1 h-px ${dividerClass}`} />
              </div>
            )}
            {group.label && collapsed && gIdx > 0 && (
              <div className={`mx-3 my-2 h-px ${dividerClass}`} />
            )}
            {group.items.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                    collapsed ? 'justify-center' : ''
                  } ${isActive ? activeClass : inactiveClass}`
                }
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={item.icon} />
                </div>
                {!collapsed && <span className="text-sm">{item.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className={`border-t p-3 ${isSuperAdmin ? 'border-amber-700' : 'border-foret-500'}`}>
        {onToggle && (
          <button
            onClick={onToggle}
            title={collapsed ? "Développer le menu" : "Réduire le menu"}
            className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm transition-colors cursor-pointer mb-1 ${collapsed ? 'justify-center' : ''} ${
              isSuperAdmin ? 'text-amber-300 hover:text-white hover:bg-amber-700/50' : 'text-foret-200 hover:text-white hover:bg-foret-500'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} />
            </div>
            {!collapsed && <span>Réduire</span>}
          </button>
        )}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm transition-colors cursor-pointer ${collapsed ? 'justify-center' : ''} ${
            isSuperAdmin ? 'text-amber-300 hover:text-red-300 hover:bg-amber-700/50' : 'text-foret-200 hover:text-red-300 hover:bg-foret-500'
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-logout-box-line" /></div>
          {!collapsed && <span>Se déconnecter</span>}
        </button>

        {!collapsed && appVersion && (
          <div className={`mt-4 px-2 py-1.5 rounded-lg bg-black/10 border ${isSuperAdmin ? 'border-amber-700/30' : 'border-white/10'} text-[9px] font-bold text-center opacity-60`}>
             <p className="uppercase tracking-widest text-white/50 mb-0.5">Atelier Bassoum Build</p>
             <p className="text-white">v.{appVersion.version}</p>
             <p className="text-[8px] font-normal italic opacity-70">{appVersion.date}</p>
          </div>
        )}
      </div>
    </aside>
  );
}
